/**
 * The speed slider (spec 035 D-1): a real range input from Slowest to
 * Fastest with Normal (1×) in the middle. `plain` (the Pictures view) shows
 * the label only; `detail` (the States panel, the sheet editor) shows the
 * multiplier and, with a cut, the played range's rate and cycle. A drag
 * commits once, when it ends; a keyboard or programmatic change at once.
 * A speed of 1 is reported as `undefined` (an absent `speed`).
 */
import React, { useEffect, useRef, useState } from 'react';
import type { SheetCut, StatePlayback } from '../../../../Shared/src/@types';
import { cycleText, positionFromSpeed, speedFromPosition, speedLabel, speedText } from '../../utils/playbackSpeed';
import styles from './Sheets.module.scss';

export interface SpeedSliderProps {
  speed: number | undefined;
  onChange: (speed: number | undefined) => void;
  variant: 'plain' | 'detail';
  /** detail: the cut and playback the line under the track describes. */
  cut?: SheetCut;
  playback?: StatePlayback;
  /** detail: the scale's ends under the track ("0.25×", "1×", "3×") instead of the cycle line. */
  scale?: boolean;
  disabled?: boolean;
  testId?: string;
}

const SpeedSlider: React.FC<SpeedSliderProps> = ({ speed, onChange, variant, cut, playback, scale, disabled, testId = 'speed-slider' }) => {
  const [position, setPosition] = useState(() => positionFromSpeed(speed));
  const dragging = useRef(false);
  useEffect(() => {
    if (!dragging.current) setPosition(positionFromSpeed(speed));
  }, [speed]);

  const shown = speedFromPosition(position);
  const label = speedLabel(shown);
  const commit = (p: number) => {
    const v = speedFromPosition(p);
    const next = v === 1 ? undefined : v;
    if ((speed ?? 1) !== v) onChange(next);
  };
  const end = () => {
    if (!dragging.current) return;
    dragging.current = false;
    commit(position);
  };

  return (
    <div className={styles.speed} data-testid={testId}>
      <div className={styles.speedHead}>
        <span className={styles.speedTitle}>Speed</span>
        <span data-testid={`${testId}-label`}>{variant === 'plain' ? label : `${label} · ${speedText(shown)}`}</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={position}
        aria-label="Speed"
        aria-valuetext={label}
        disabled={disabled}
        onPointerDown={() => { dragging.current = true; }}
        onPointerUp={end}
        onPointerCancel={end}
        onBlur={end}
        onChange={(e) => {
          const p = Number(e.target.value);
          setPosition(p);
          if (!dragging.current) commit(p);
        }}
      />
      {variant === 'plain' ? (
        <div className={styles.speedScale} aria-hidden><span>Slowest</span><span>Normal</span><span>Fastest</span></div>
      ) : scale ? (
        <div className={styles.speedScale} aria-hidden><span>0.25×</span><span>1×</span><span>3×</span></div>
      ) : cut ? (
        <div className={styles.speedLine} data-testid={`${testId}-cycle`}>
          {cycleText(cut, { ...(playback ?? { mode: 'loop' }), ...(shown === 1 ? { speed: undefined } : { speed: shown }) })}
        </div>
      ) : null}
    </div>
  );
};

export default SpeedSlider;
